import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import AdminLayout from './components/layout/AdminLayout';

// Public Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import CategoriesPage from './pages/CategoriesPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Protected Pages
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import WishlistPage from './pages/WishlistPage';
import CartPage from './pages/CartPage';

// Admin Pages
import DashboardPage from './pages/admin/DashboardPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import DemoPhotoManager from './components/admin/DemoPhotoManager';
import AdminNewsletterPage from './pages/admin/AdminNewsletterPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import { analyticsAPI } from './services/api';

// Route Protection
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';

function App() {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storageKey = 'tolou_last_visit_tracked';
    const today = new Date().toISOString().slice(0, 10);
    const alreadyTracked = window.localStorage.getItem(storageKey);

    if (alreadyTracked === today) {
      return;
    }

    const payload = {
      path: window.location.pathname,
      referrer: document.referrer || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    };

    analyticsAPI
      .trackVisit(payload)
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Visit tracking failed', error);
        }
      })
      .finally(() => {
        try {
          window.localStorage.setItem(storageKey, today);
        } catch (_) {
          /* ignore storage errors */
        }
      });
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <Routes>
        {/* Admin Routes - No Header/Footer */}
        <Route path="/admin/*" element={
          <AdminRoute>
            <AdminLayout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/products" element={<AdminProductsPage />} />
                <Route path="/analytics" element={<AdminAnalyticsPage />} />
                <Route path="/categories" element={<AdminCategoriesPage />} />
                <Route path="/orders" element={<AdminOrdersPage />} />
                <Route path="/users" element={<AdminUsersPage />} />
                <Route path="/newsletter" element={<AdminNewsletterPage />} />
                <Route path="/settings" element={<AdminSettingsPage />} />
                <Route path="/demo-photos" element={<DemoPhotoManager />} />
              </Routes>
            </AdminLayout>
          </AdminRoute>
        } />
        
        {/* Public Routes - With Header/Footer */}
        <Route path="/*" element={
          <>
            <Header />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/categories/:id" element={<CategoriesPage />} />
                <Route path="/products/:id" element={<ProductDetailPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                
                {/* Auth Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                
                {/* Protected Routes */}
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } />
                <Route path="/orders" element={
                  <ProtectedRoute>
                    <OrdersPage />
                  </ProtectedRoute>
                } />
                <Route path="/orders/:id" element={
                  <ProtectedRoute>
                    <OrderDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="/wishlist" element={
                  <ProtectedRoute>
                    <WishlistPage />
                  </ProtectedRoute>
                } />
                <Route path="/cart" element={
                  <ProtectedRoute>
                    <CartPage />
                  </ProtectedRoute>
                } />
              </Routes>
            </main>
            <Footer />
          </>
        } />
      </Routes>
    </div>
  );
}

export default App; 